'use client'

import { useState, useEffect } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations, useLocale } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import {
  CheckCheckIcon,
  TrashIcon,
  BellRingIcon,
  InfoIcon,
  TriangleAlert,
  CircleXIcon,
  SquareCheckBigIcon
} from 'lucide-react'
import { toast } from 'sonner'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { formatNotification } from '@/lib/formatter/notification-formatter'
import { formatDateTime } from '@/lib/date-utils'
import { tOptional } from '@/lib/utils'
import { NotificationType } from '@/types/notification'
import { NotificationTypeEnum } from '@/enums'
import { useSetReadNotifications, useDeleteNotification } from '@/hooks/use-notifications'
import { DashboardResponseType } from '@/types/dashboard'

type NotificationItemProps = {
  item: NotificationType
  isSelected?: boolean
  onToggleSelect?: (id: string) => void
  onDeleteSuccess?: () => void
  requireDeleteConfirmation?: boolean
  onDeleteConfirmation?: (id: string) => void
  showCheckbox?: boolean
  onNavigate?: () => void
}

const NotificationItem = ({
  item,
  isSelected = false,
  onToggleSelect,
  onDeleteSuccess,
  requireDeleteConfirmation = false,
  onDeleteConfirmation,
  showCheckbox = true,
  onNavigate
}: NotificationItemProps) => {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const queryClient = useQueryClient()

  // Local state for optimistic UI update
  const [isRead, setIsRead] = useState(!!item.readAt)

  // Sync local state with prop when item.readAt changes (cache updated)
  useEffect(() => {
    setIsRead(!!item.readAt)
  }, [item.readAt])

  const { mutate: markAsRead } = useSetReadNotifications()
  const { mutate: deleteNotification } = useDeleteNotification()

  const formatted = formatNotification(item)

  const getTypeIcon = (type?: NotificationTypeEnum) => {
    switch (type) {
      case NotificationTypeEnum.ERROR:
        return (
          <AvatarFallback className="bg-red-400 text-white">
            <CircleXIcon className="h-3.5 w-3.5" />
          </AvatarFallback>
        )
      case NotificationTypeEnum.WARNING:
        return (
          <AvatarFallback className="bg-amber-400 text-white">
            <TriangleAlert className="h-3.5 w-3.5" />
          </AvatarFallback>
        )
      case NotificationTypeEnum.SUCCESS:
        return (
          <AvatarFallback className="bg-green-400 text-white">
            <SquareCheckBigIcon className="h-3.5 w-3.5" />
          </AvatarFallback>
        )
      case NotificationTypeEnum.INFO:
        return (
          <AvatarFallback className="bg-blue-400 text-white">
            <InfoIcon className="h-3.5 w-3.5" />
          </AvatarFallback>
        )
      default:
        return (
          <AvatarFallback className="bg-emerald-400 text-white">
            <BellRingIcon className="h-3.5 w-3.5" />
          </AvatarFallback>
        )
    }
  }

  const handleMarkAsRead = (navigateToLink?: string) => {
    const navigate = () => {
      if (navigateToLink) {
        onNavigate?.()
        router.push(navigateToLink)
      }
    }

    // Already read → just navigate.
    if (isRead) {
      navigate()
      return
    }

    setIsRead(true)

    // Update every cache OPTIMISTICALLY and synchronously, BEFORE navigating. Clicking a notification
    // closes the dropdown (unmounts this item), so a mutation onSuccess callback would never run — which is
    // why the list stayed "unread" and the header/sidebar counter (synced from the dropdown-notifications
    // cache via a useEffect) never decremented, even though the backend already marked it read.
    const now = new Date().toISOString()

    queryClient.getQueriesData({ queryKey: ['notifications'] }).forEach(([queryKey, oldData]: [any, any]) => {
      if (oldData?.items?.some((n: NotificationType) => n.id === item.id)) {
        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: oldData.items.map((notif: NotificationType) =>
            notif.id === item.id ? { ...notif, readAt: now } : notif
          )
        })
      }
    })

    queryClient.setQueriesData({ queryKey: ['dropdown-notifications'] }, (oldData: any) => {
      if (!oldData?.items?.some((n: NotificationType) => n.id === item.id)) {
        return oldData
      }
      return {
        ...oldData,
        unreadTotal: Math.max(0, (oldData.unreadTotal || 0) - 1),
        items: oldData.items.map((notif: NotificationType) =>
          notif.id === item.id ? { ...notif, readAt: now } : notif
        )
      }
    })

    queryClient.setQueryData<DashboardResponseType>(['dashboard'], old => {
      if (!old?.stats) {
        return old
      }
      return {
        ...old,
        stats: {
          ...old.stats,
          unreadNotifications: Math.max(0, (old.stats.unreadNotifications ?? 0) - 1)
        }
      }
    })

    // Persist to the backend (fire-and-forget; the UI is already updated above). Roll back on failure.
    markAsRead(
      { params: { ids: [item.id] } },
      {
        onError: () => {
          setIsRead(false)
          toast.error(t('error_marking_as_read'))
          queryClient.invalidateQueries({ queryKey: ['notifications'] })
          queryClient.invalidateQueries({ queryKey: ['dropdown-notifications'] })
          queryClient.invalidateQueries({ queryKey: ['dashboard'] })
        }
      }
    )

    navigate()
  }

  const handleDelete = () => {
    if (requireDeleteConfirmation && onDeleteConfirmation) {
      // List mode: require confirmation
      onDeleteConfirmation(item.id)
    } else {
      // Dropdown mode: delete directly
      deleteNotification(item.id, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['notifications'] })

          queryClient.setQueriesData({ queryKey: ['dropdown-notifications'] }, (oldData: any) => {
            if (!oldData) {
              return oldData
            }
            const wasUnread = !item.readAt
            return {
              ...oldData,
              total: Math.max(0, (oldData.total || 0) - 1),
              unreadTotal: wasUnread ? Math.max(0, (oldData.unreadTotal || 0) - 1) : oldData.unreadTotal,
              items: oldData.items.filter((notif: NotificationType) => notif.id !== item.id)
            }
          })

          await queryClient.invalidateQueries({
            queryKey: ['dropdown-notifications'],
            refetchType: 'none'
          })

          toast.success(t('deleted_successfully'))
          onDeleteSuccess?.()
        },
        onError: async (error: any) => {
          // If 404, item already deleted - just update cache without showing error
          if (error.response?.status === 404) {
            await queryClient.invalidateQueries({ queryKey: ['notifications'] })

            queryClient.setQueriesData({ queryKey: ['dropdown-notifications'] }, (oldData: any) => {
              if (!oldData) {
                return oldData
              }
              const wasUnread = !item.readAt
              return {
                ...oldData,
                total: Math.max(0, (oldData.total || 0) - 1),
                unreadTotal: wasUnread ? Math.max(0, (oldData.unreadTotal || 0) - 1) : oldData.unreadTotal,
                items: oldData.items.filter((notif: NotificationType) => notif.id !== item.id)
              }
            })

            await queryClient.invalidateQueries({
              queryKey: ['dropdown-notifications'],
              refetchType: 'none'
            })

            onDeleteSuccess?.()
          } else {
            toast.error(error.response?.data.message ?? t('error_deleting'))
          }
        }
      })
    }
  }

  return (
    <div
      className={`flex items-start justify-items-start gap-4 bg-white py-4 hover:bg-gray-50 dark:bg-neutral-900 dark:hover:bg-neutral-950 ${isRead ? 'opacity-60' : ''}`}>
      {showCheckbox && onToggleSelect && (
        <div className="flex shrink-0 items-center gap-2 self-center pl-2">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(item.id)}
            aria-label={`select-${item.id}`}
          />
        </div>
      )}
      <Avatar className="ml-2 h-9 w-9">{getTypeIcon(item.type)}</Avatar>
      <div className="min-w-0 flex-1">
        <div
          className={formatted.link ? 'cursor-pointer' : ''}
          onClick={e => {
            e.stopPropagation()
            if (formatted.link) {
              if (!isRead) {
                handleMarkAsRead(formatted.link)
              } else {
                if (onNavigate) {
                  onNavigate()
                }
                router.push(formatted.link)
              }
            }
          }}>
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="text-sm leading-none font-bold">{tOptional(t, formatted.title)}</span>
          </div>
          {formatted.message && (
            <p className="text-muted-foreground line-clamp-2 text-xs">{tOptional(t, formatted.message)}</p>
          )}
          {formatted.additionalParams &&
            formatted.additionalParams.map((param, index) => {
              return (
                <p key={index} className="text-muted-foreground line-clamp-2 text-xs">
                  {param.values ? tOptional(t, param.key, param.values) : tOptional(t, param.key)}
                </p>
              )
            })}
          <div className="text-muted-foreground mt-2 text-xs">{formatDateTime(item.createdAt, locale)}</div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 self-start pr-2">
        {!isRead && (
          <Button
            variant="ghost"
            size="icon"
            title={t('mark_as_read')}
            aria-label="mark-as-read"
            onClick={() => handleMarkAsRead()}>
            <CheckCheckIcon className="h-4 w-4" />
          </Button>
        )}
        <Button variant="ghost" size="icon" title={t('delete')} aria-label="delete" onClick={handleDelete}>
          <TrashIcon className="text-destructive h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export default NotificationItem
